import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Mail, Phone, MapPin, Loader2 } from "lucide-react";

const TABS = [
  { v: "pending", l: "En attente" },
  { v: "approved", l: "Approuvés" },
  { v: "rejected", l: "Refusés" },
];

export default function AdminPartnersPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/partners", { params: { status: tab } });
      setItems(data);
    } catch (err) {
      toast.error("Accès refusé");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]);

  if (!user) return null;
  if (user.role !== "admin") {
    return (
      <div className="pt-32 text-center">
        <div className="text-neutral-500">Réservé aux administrateurs</div>
      </div>
    );
  }

  const decide = async (id, decision, reason) => {
    try {
      await api.post(`/admin/partners/${id}/${decision}`, { reason });
      toast.success(decision === "approve" ? "Partenaire validé" : "Partenaire refusé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="pt-24 sm:pt-28 min-h-screen bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Admin</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mb-8">Modération partenaires</h1>

        <div className="flex gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.v}
              data-testid={`admp-tab-${t.v}`}
              onClick={() => setTab(t.v)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border ${tab === t.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-neutral-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-black/15 p-12 text-center text-neutral-500">Aucun dossier dans cet état</div>
        ) : (
          <div className="space-y-4">
            {items.map((p) => (
              <div key={p.id} className="border border-black/10 bg-white p-6" data-testid={`adm-partner-${p.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">
                        {p.partner_type === "conciergerie" ? "Conciergerie" : "Ménage"} · {p.legal_status}
                      </div>
                      {p.insurance && <span className="text-[9px] uppercase tracking-[0.15em] font-bold bg-[#166534] text-white px-1.5 py-0.5">Assuré</span>}
                    </div>
                    <div className="font-display text-xl font-bold">{p.company_name}</div>
                    <div className="text-sm text-neutral-600 mt-1">{p.contact_name}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 mt-3 text-xs text-neutral-700">
                      <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {p.email}</div>
                      <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {p.phone}</div>
                      <div className="flex items-center gap-1.5 col-span-2"><MapPin className="w-3 h-3" /> {[...(p.regions || []), ...(p.cities || [])].join(" · ")}</div>
                      {p.siret && <div className="col-span-2">SIRET : {p.siret}</div>}
                    </div>
                    <p className="text-sm text-neutral-700 mt-3 line-clamp-3">{p.description}</p>
                  </div>
                  {tab === "pending" && (
                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => decide(p.id, "approve")}
                        data-testid={`adm-approve-${p.id}`}
                        className="bg-[#166534] text-white px-4 py-2 font-semibold flex items-center gap-2 hover:bg-[#0F5128]"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approuver
                      </button>
                      <button
                        onClick={() => {
                          const reason = window.prompt("Motif du refus (optionnel) :", "");
                          decide(p.id, "reject", reason || undefined);
                        }}
                        data-testid={`adm-reject-${p.id}`}
                        className="border-2 border-[#991B1B] text-[#991B1B] px-4 py-2 font-semibold flex items-center gap-2 hover:bg-[#991B1B] hover:text-white"
                      >
                        <XCircle className="w-4 h-4" /> Refuser
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
